import React, { useState } from 'react';
import { useResponsive } from '../hooks/useResponsive';

const Settings = ({ timeSettings, onSaveTimeSettings }) => {
  const viewport = useResponsive();
  const [isOpen, setIsOpen] = useState(false);
  const [greenValue, setGreenValue] = useState(timeSettings.green);
  const [yellowValue, setYellowValue] = useState(timeSettings.yellow);
  const [redValue, setRedValue] = useState(timeSettings.red);
  
  // Handler for green value change
  const handleGreenChange = (e) => {
    const newGreen = parseInt(e.target.value);
    setGreenValue(e.target.value); // Allow any input initially
    
    // Auto-adjust other values if needed
    const yellowInt = parseInt(yellowValue);
    if (newGreen >= yellowInt) {
      setYellowValue(newGreen + 1);
      
      // Also adjust red if needed
      const redInt = parseInt(redValue);
      if (newGreen + 1 >= redInt) {
        setRedValue(newGreen + 2);
      }
    }
  };

  // Handler for yellow value change
  const handleYellowChange = (e) => {
    const newYellow = parseInt(e.target.value);
    const greenInt = parseInt(greenValue);
    
    // Ensure yellow is always > green
    if (newYellow <= greenInt) {
      setYellowValue(greenInt + 1);
    } else {
      setYellowValue(e.target.value);
      
      // Auto-adjust red if needed
      const redInt = parseInt(redValue);
      if (newYellow >= redInt) {
        setRedValue(newYellow + 1);
      }
    }
  };

  // Handler for red value change
  const handleRedChange = (e) => {
    const newRed = parseInt(e.target.value);
    const yellowInt = parseInt(yellowValue);
    
    // Ensure red is always > yellow
    if (newRed <= yellowInt) {
      setRedValue(yellowInt + 1);
    } else {
      setRedValue(e.target.value);
    }
  };
  
  // Handle save with validation
  const handleSave = () => {
    // Convert to integers for comparison
    const greenInt = parseInt(greenValue);
    const yellowInt = parseInt(yellowValue);
    const redInt = parseInt(redValue);
    
    // Validate relationships - enforce constraints
    let adjustedYellow = yellowInt;
    let adjustedRed = redInt;
    
    // Ensure yellow is at least 1 more than green
    if (adjustedYellow <= greenInt) {
      adjustedYellow = greenInt + 1;
    }
    
    // Ensure red is at least 1 more than yellow
    if (adjustedRed <= adjustedYellow) {
      adjustedRed = adjustedYellow + 1;
    }
    
    // If values were adjusted, update state
    if (adjustedYellow !== yellowInt) {
      setYellowValue(adjustedYellow);
    }
    
    if (adjustedRed !== redInt) {
      setRedValue(adjustedRed);
    }
    
    // Save the valid values (either original or adjusted)
    onSaveTimeSettings({
      green: greenInt,
      yellow: adjustedYellow,
      red: adjustedRed
    });
  };

  // Add this function right after handleSave
  const handleResetToDefaults = () => {
    setGreenValue(7);
    setYellowValue(14);
    setRedValue(15);

    // Also save the defaults immediately
    onSaveTimeSettings({
      green: 7,
      yellow: 14,
      red: 15
    });
  };

  // Responsive styles - aligned with SkipEye design system
  const styles = {
    container: {
      marginTop: viewport.isMobile ? 'var(--space-md)' : '20px'
    },
    header: {
      padding: viewport.isMobile ? '14px 16px' : '12px 16px',
      backgroundColor: '#F5F5F4',
      border: '1px solid #E7E5E4',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isOpen ? (viewport.isMobile ? 'var(--space-sm)' : '12px') : '0',
      minHeight: '44px',
      transition: 'background-color 0.15s ease'
    },
    headerTitle: {
      margin: 0,
      fontSize: viewport.isMobile ? 'var(--text-base)' : '14px',
      fontWeight: '500',
      color: '#44403C'
    },
    chevron: {
      fontSize: viewport.isMobile ? '12px' : '10px',
      color: '#78716C',
      transition: 'transform 0.2s ease'
    },
    content: {
      padding: viewport.isMobile ? '0 var(--space-sm) var(--space-md)' : '0'
    },
    settingItem: {
      marginBottom: viewport.isMobile ? 'var(--space-md)' : '16px'
    },
    label: {
      display: 'block',
      marginBottom: viewport.isMobile ? '8px' : '6px',
      fontSize: '12px',
      fontWeight: '500',
      color: '#78716C',
      textTransform: 'uppercase',
      letterSpacing: '0.025em'
    },
    input: {
      width: '100%',
      padding: viewport.isMobile ? '12px 14px' : '10px 12px',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      fontSize: viewport.isMobile ? '16px' : '14px',
      minHeight: '44px',
      boxSizing: 'border-box',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      backgroundColor: '#FAFAF9',
      color: '#292524',
      fontFamily: 'inherit'
    },
    saveButton: {
      width: '100%',
      padding: viewport.isMobile ? '12px 20px' : '10px 16px',
      backgroundColor: '#166534',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: viewport.isMobile ? 'var(--text-sm)' : '14px',
      minHeight: '44px',
      transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
      boxShadow: '0 1px 2px rgba(28, 25, 23, 0.05)',
      letterSpacing: '0.025em'
    },
    resetButton: {
      width: '100%',
      padding: viewport.isMobile ? '12px 20px' : '10px 16px',
      backgroundColor: 'white',
      color: '#57534E',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: viewport.isMobile ? 'var(--text-sm)' : '14px',
      fontWeight: '500',
      marginTop: viewport.isMobile ? 'var(--space-sm)' : '8px',
      minHeight: '44px',
      transition: 'background-color 0.15s ease, border-color 0.15s ease'
    }
  };

  return (
    <div className="settings-container" style={styles.container}>
      {/* Settings Header - Clickable to expand/collapse */}
      <div
        className="settings-header"
        onClick={() => setIsOpen(!isOpen)}
        style={styles.header}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#EEEEEC'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#F5F5F4'}
      >
        <h3 style={styles.headerTitle}>Hire Alerts</h3>
        <span style={{
          ...styles.chevron,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>
          ▼
        </span>
      </div>

      {/* Settings Content - Collapsible */}
      {isOpen && (
        <div className="settings-content" style={styles.content}>
          <div className="setting-item" style={styles.settingItem}>
            <label htmlFor="green-value" style={styles.label}>
              Green (days)
            </label>
            <input
              id="green-value"
              type="number"
              min="1"
              value={greenValue}
              onChange={handleGreenChange}
              style={styles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#166534';
                e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D6D3D1';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div className="setting-item" style={styles.settingItem}>
            <label htmlFor="yellow-value" style={styles.label}>
              Yellow (days)
            </label>
            <input
              id="yellow-value"
              type="number"
              min="1"
              value={yellowValue}
              onChange={handleYellowChange}
              style={styles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#166534';
                e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D6D3D1';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div className="setting-item" style={styles.settingItem}>
            <label htmlFor="red-value" style={styles.label}>
              Red (days)
            </label>
            <input
              id="red-value"
              type="number"
              min="1"
              value={redValue}
              onChange={handleRedChange}
              style={styles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#166534';
                e.target.style.boxShadow = '0 0 0 3px rgba(22, 101, 52, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D6D3D1';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <button
            onClick={handleSave}
            style={styles.saveButton}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#14532D';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(22, 101, 52, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#166534';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(28, 25, 23, 0.05)';
            }}
          >
            Save
          </button>

          <button
            onClick={handleResetToDefaults}
            style={styles.resetButton}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#F5F5F4';
              e.currentTarget.style.borderColor = '#A8A29E';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#D6D3D1';
            }}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
};

export default Settings;